import { z } from 'zod';
import { FIELD_LIMITS } from './constants.js';
import {
  compensationPeriodSchema,
  employmentTypeSchema,
  jobStatusSchema,
  workplaceTypeSchema,
  type CompensationPeriod,
  type EmploymentType,
  type JobStatus,
  type WorkplaceType,
} from './enums.js';
import type { JobQuestionDto } from './question.js';

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Slug must be at least 3 characters')
  .max(FIELD_LIMITS.jobSlug)
  .regex(SLUG_PATTERN, 'Use lowercase letters, numbers, and single hyphens');

/** Combining marks left behind by NFKD decomposition (U+0300-U+036F). */
function stripCombiningMarks(value: string): string {
  return Array.from(value)
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code < 0x0300 || code > 0x036f;
    })
    .join('');
}

/** Best-effort slug suggestion; the admin can always override it. */
export function slugify(input: string): string {
  return stripCombiningMarks(input.normalize('NFKD'))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, FIELD_LIMITS.jobSlug);
}

/**
 * Empty strings and nulls arriving from HTML number inputs mean "not provided",
 * never zero, so they are mapped to null before any numeric coercion runs.
 */
const nullableInt = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? null : value),
  z.coerce.number().int().min(0).max(100_000_000).nullable(),
);

const markdownField = (max: number) =>
  z
    .string()
    .max(max)
    .nullish()
    .transform((value) => {
      const trimmed = value?.trim();
      return trimmed ? trimmed : null;
    });

const jobBaseSchema = z.object({
  title: z.string().trim().min(2, 'Title is required').max(FIELD_LIMITS.jobTitle),
  slug: slugSchema,
  department: z.string().trim().min(1, 'Department is required').max(FIELD_LIMITS.jobDepartment),
  locationText: z.string().trim().min(1, 'Location is required').max(FIELD_LIMITS.jobLocation),
  workplaceType: workplaceTypeSchema,
  employmentType: employmentTypeSchema,
  summary: z.string().trim().min(1, 'Summary is required').max(FIELD_LIMITS.jobSummary),
  description: z.string().trim().min(1, 'Description is required').max(FIELD_LIMITS.jobLongText),
  responsibilities: markdownField(FIELD_LIMITS.jobLongText),
  requiredQualifications: markdownField(FIELD_LIMITS.jobLongText),
  preferredQualifications: markdownField(FIELD_LIMITS.jobLongText),
  hiringProcessNote: markdownField(FIELD_LIMITS.jobLongText),
  compensationMin: nullableInt,
  compensationMax: nullableInt,
  compensationCurrency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Use a three-letter currency code')
    .nullish()
    .default(null),
  compensationPeriod: compensationPeriodSchema.nullish().default(null),
  compensationVisible: z.boolean().default(false),
  coverLetterRequired: z.boolean().default(false),
  closesAt: z.iso.date().nullish().default(null),
});

function refineCompensation(value: z.infer<typeof jobBaseSchema>, ctx: z.RefinementCtx): void {
  const { compensationMin, compensationMax, compensationVisible, compensationCurrency, compensationPeriod } =
    value;
  if (compensationMin != null && compensationMax != null && compensationMin > compensationMax) {
    ctx.addIssue({
      code: 'custom',
      path: ['compensationMax'],
      message: 'Maximum must be greater than or equal to minimum',
    });
  }
  if (!compensationVisible) return;
  if (compensationMin == null && compensationMax == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['compensationMin'],
      message: 'Add a minimum or maximum before showing compensation publicly',
    });
  }
  if (!compensationCurrency) {
    ctx.addIssue({
      code: 'custom',
      path: ['compensationCurrency'],
      message: 'Currency is required when compensation is public',
    });
  }
  if (!compensationPeriod) {
    ctx.addIssue({
      code: 'custom',
      path: ['compensationPeriod'],
      message: 'Period is required when compensation is public',
    });
  }
}

export const jobCreateSchema = jobBaseSchema.superRefine(refineCompensation);
export type JobCreateInput = z.infer<typeof jobCreateSchema>;

export const jobUpdateSchema = jobBaseSchema.superRefine(refineCompensation);
export type JobUpdateInput = z.infer<typeof jobUpdateSchema>;

export const jobDuplicateSchema = z.object({
  title: z.string().trim().min(2).max(FIELD_LIMITS.jobTitle).optional(),
  slug: slugSchema.optional(),
});
export type JobDuplicateInput = z.infer<typeof jobDuplicateSchema>;

export const adminJobListQuerySchema = z.object({
  status: jobStatusSchema.optional(),
  q: z.string().trim().max(160).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type AdminJobListQuery = z.infer<typeof adminJobListQuerySchema>;

export interface JobCompensation {
  min: number | null;
  max: number | null;
  currency: string | null;
  period: CompensationPeriod | null;
}

/** Fields safe to expose on public endpoints. */
export interface PublicJobSummary {
  slug: string;
  title: string;
  department: string;
  locationText: string;
  workplaceType: WorkplaceType;
  employmentType: EmploymentType;
  status: Extract<JobStatus, 'PUBLISHED' | 'CLOSED'>;
  publishedAt: string | null;
  closesAt: string | null;
  compensation: JobCompensation | null;
}

/**
 * Public detail. Long-form fields arrive as HTML that the API has already
 * rendered from Markdown and sanitised; the browser sanitises again before
 * inserting it.
 */
export interface PublicJobDetail extends PublicJobSummary {
  summary: string;
  descriptionHtml: string;
  responsibilitiesHtml: string | null;
  requiredQualificationsHtml: string | null;
  preferredQualificationsHtml: string | null;
  hiringProcessNoteHtml: string | null;
  coverLetterRequired: boolean;
  acceptingApplications: boolean;
  questions: JobQuestionDto[];
  closedAt: string | null;
  updatedAt: string;
}

export interface AdminJobSummary extends Omit<PublicJobSummary, 'status'> {
  id: string;
  status: JobStatus;
  applicationCount: number;
  newApplicationCount: number;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

/** Admin detail carries the raw Markdown so the editor round-trips losslessly. */
export interface AdminJobDetail {
  id: string;
  slug: string;
  title: string;
  department: string;
  locationText: string;
  workplaceType: WorkplaceType;
  employmentType: EmploymentType;
  status: JobStatus;
  summary: string;
  description: string;
  responsibilities: string | null;
  requiredQualifications: string | null;
  preferredQualifications: string | null;
  hiringProcessNote: string | null;
  compensationMin: number | null;
  compensationMax: number | null;
  compensationCurrency: string | null;
  compensationPeriod: CompensationPeriod | null;
  compensationVisible: boolean;
  coverLetterRequired: boolean;
  closesAt: string | null;
  questions: JobQuestionDto[];
  applicationCount: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  closedAt: string | null;
}
